import React, { forwardRef, useImperativeHandle, useRef, useState, useCallback } from 'react'
import { useAppSelector } from 'reduxTyped'
import { l } from '@manychat/manyui'

import { ContextualHint } from 'apps/aiAutomation/components/EngagementGoalEditor/components/ContextualHint'
import { EditorStepLayout } from 'apps/aiAutomation/components/EngagementGoalEditor/components/EditorStepLayout/EditorStepLayout'
import {
  ContextEditorFormValues,
  MessageEditorFormValues,
} from 'apps/aiAutomation/components/EngagementGoalEditor/components/LeadGenEditor/lib/types'
import { useGoalEditorContext } from 'apps/aiAutomation/components/EngagementGoalEditor/lib/context'
import {
  GoalEditorMode,
  LeadGenEditorStep,
  SubmitRef,
} from 'apps/aiAutomation/components/EngagementGoalEditor/lib/types'
import { AiAutomationEngagementGoalSelectors } from 'apps/aiAutomation/store/engagementGoal'
import { EngagementGoal, GoalStatus, GoalType } from 'shared/api/requests/aiEngagementGoal/schemas'

import { useSelectedGoalData } from '../../hooks/useSelectedGoalData'
import { EditorLayout } from '../EditorLayout/EditorLayout'
import { PublishManager } from '../PublishManager/PublishManager'

import { ContextEditor } from './components/ContextEditor/ContextEditor'
import { MessageEditor } from './components/MessageEditor/MessageEditor'
import { useLeadGenEditor } from './hooks/useLeadGenEditor'

export const LeadGenEditor = forwardRef<SubmitRef<EngagementGoal>>((_, ref) => {
  const { stepsState, contextData, contentData, onContentFinish, onContextFinish, combineAllData } =
    useLeadGenEditor()
  const [isContextDirty, setContextDirty] = useState(false)

  const { mode, changeMode } = useGoalEditorContext(GoalType.LEAD_GEN)
  const isReadMode = mode === GoalEditorMode.READ
  const isCreateMode = mode === GoalEditorMode.CREATE

  const contextRef = useRef<SubmitRef<ContextEditorFormValues>>(null)
  const messageRef = useRef<SubmitRef<MessageEditorFormValues>>(null)

  const activeGoalsLimitReached = useAppSelector(
    AiAutomationEngagementGoalSelectors.getIsActiveGoalLimitReached,
  )
  const { selectedGoal } = useSelectedGoalData()
  const isPublished = selectedGoal ? selectedGoal.status === GoalStatus.ACTIVE : false
  const isPublishBlocked = activeGoalsLimitReached && !isPublished

  const isContextStepDimmed =
    isCreateMode &&
    stepsState.completed[LeadGenEditorStep.MESSAGE] &&
    stepsState.current === LeadGenEditorStep.MESSAGE

  const isMessageStepDimmed =
    isContextDirty ||
    (isCreateMode &&
      stepsState.completed[LeadGenEditorStep.CONTEXT] &&
      stepsState.current === LeadGenEditorStep.CONTEXT)

  const isPublishStepDimmed =
    isContextDirty ||
    (isCreateMode &&
      stepsState.completed[LeadGenEditorStep.CONTEXT] &&
      [LeadGenEditorStep.CONTEXT, LeadGenEditorStep.MESSAGE].includes(stepsState.current))

  const onContextDirtyChange = useCallback(
    (value: boolean) => {
      setContextDirty(value)
      if (mode === GoalEditorMode.EDIT || mode === GoalEditorMode.SAVING_BLOCKED) {
        changeMode(value ? GoalEditorMode.SAVING_BLOCKED : GoalEditorMode.EDIT)
      }
    },
    [changeMode, mode],
  )

  useImperativeHandle(
    ref,
    () => ({
      submit: async () => {
        if (!contextRef.current?.submit || !messageRef.current?.submit) {
          return { isValid: false }
        }

        const context = await contextRef.current.submit()
        const message = await messageRef.current.submit()

        return combineAllData(context, message)
      },
    }),
    [combineAllData],
  )

  return (
    <div className="o-auto p-x p-y-md">
      <ContextualHint />

      <EditorLayout title={l.translate('Set it up in 3 quick steps')}>
        <EditorStepLayout
          number={1}
          step={LeadGenEditorStep.CONTEXT}
          stepsState={stepsState}
          isDimmed={isContextStepDimmed}
        >
          <ContextEditor
            isActive={
              stepsState.current === LeadGenEditorStep.CONTEXT ||
              (!isReadMode && stepsState.completed[LeadGenEditorStep.CONTEXT])
            }
            onFinish={onContextFinish}
            contextData={contextData}
            onDirtyChange={onContextDirtyChange}
            ref={contextRef}
          />
        </EditorStepLayout>

        <EditorStepLayout
          number={2}
          step={LeadGenEditorStep.MESSAGE}
          stepsState={stepsState}
          isDimmed={isMessageStepDimmed}
          className="m-t-lg"
        >
          <MessageEditor
            contextData={contextData}
            contentData={contentData}
            isActive={
              stepsState.current === LeadGenEditorStep.MESSAGE ||
              (!isReadMode && stepsState.completed[LeadGenEditorStep.MESSAGE])
            }
            isPublishBlocked={isPublishBlocked}
            onFinish={onContentFinish}
            ref={messageRef}
          />
        </EditorStepLayout>

        <EditorStepLayout
          number={3}
          step={LeadGenEditorStep.PUBLISH}
          stepsState={stepsState}
          isDimmed={isPublishStepDimmed}
          isBlocked={isPublishBlocked}
          className="m-t-lg"
        >
          <PublishManager
            type={GoalType.LEAD_GEN}
            isBlocked={isPublishBlocked}
            isActive={
              stepsState.current === LeadGenEditorStep.PUBLISH ||
              (!isReadMode && stepsState.completed[LeadGenEditorStep.PUBLISH])
            }
          />
        </EditorStepLayout>
      </EditorLayout>
    </div>
  )
})
